/**
 * VirtualizedDiffRow - Renders a single diff file within a virtualized list.
 *
 * ## Measurement Strategy
 *
 * This component uses tanstack/react-virtual for virtualization. The virtualizer needs to know
 * each item's height, which can come from either:
 * 1. **Estimation** - via `estimateSize` callback (uses `estimateDiffSize`)
 * 2. **Measurement** - via `measureElement` ref (uses ResizeObserver)
 *
 * ### When Measurement is NOT Needed (Deterministic Height):
 *
 * - **Loading/Skeleton states**: Height is calculated from `estimateDiffSize()` based on
 *   line counts, comment threads, and annotations. No need to measure.
 *
 * - **Collapsed diffs**: Just header + collapsed indicator. Fixed height.
 *
 * ### When Measurement IS Needed (Dynamic Height):
 *
 * - **Loaded, expanded diffs**: Actual content varies from estimates. Once measured,
 *   the ResizeObserver continues watching for changes caused by:
 *   - User expands/collapses comment threads
 *   - User opens a new comment form (and types, growing the form)
 *   - Lines are expanded/collapsed
 *   - Annotations appear/disappear
 *
 * ### How ResizeObserver Works with measureElement:
 *
 * When `measureElement` is passed as a ref to an element:
 * 1. Initial measurement happens when the ref attaches
 * 2. A ResizeObserver is attached and continues observing
 * 3. Any height change triggers automatic re-measurement
 * 4. Observer is cleaned up when element unmounts
 *
 * This means we don't need special logic for "re-measure when threads change" -
 * the ResizeObserver handles it automatically.
 *
 * ### Scroll Position Adjustment:
 *
 * When item height changes, `shouldAdjustScrollPositionOnItemSizeChange` determines
 * whether to adjust scroll.
 */

import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {memo, type CSSProperties} from 'react'
import type {ProgressiveDiffEntry} from '../types/progressive-diff-types'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import type {DiffEntry} from '@github-ui/diff-lines'
import {DiffEntryLoadingSkeleton} from './progressive-diff/DiffEntryLoadingSkeleton'
import {PullRequestDiff} from './PullRequestDiff'
import {estimateDiffSize, type ThreadSizeInfoMap} from '../helpers/estimate-diff-size'
import {
  useCollapsedDiffStatus,
  useIsSelectedFile,
  useCommentsPreference,
  useLineSpacingPreference,
  useSplitPreference,
  useThreadSizeInfoMap,
} from '../stores/selectors'
import {noop} from '@github-ui/noop'
import styles from './PullRequestVirtualizedDiffRow.module.css'
import diffStyles from '@github-ui/diff-lines/Diff.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'

interface VirtualizedDiffRowProps {
  // Decomposed from the tanstack `VirtualItem` object so React.memo's shallow compare works:
  // the virtualizer recreates `VirtualItem` references whenever any row's size cache changes,
  // which would bust memo on every row. Scalars survive those object-identity changes.
  virtualRowIndex: number
  virtualRowStart: number
  virtualizedDiffEntry: ProgressiveDiffEntry | undefined
  diffEntry: DiffEntry | undefined
  diffSummary: PullRequestFileTreeDiff | undefined
  scrollMargin: number
  measureElement: (node: Element | null) => void
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  virtualWindowHeight?: number
  imageEstimateEnabled?: boolean
  rowEstimateRefinementEnabled?: boolean
  // When true, the virtualizer writes each row's `top` directly to the DOM, so we must not set
  // it inline here, and every row must register via `measureElement` so the virtualizer can
  // position it.
  directDomUpdatesEnabled?: boolean
}

const MemoizedVirtualizedDiffRow = memo(function VirtualizedDiffRow({
  virtualRowIndex,
  virtualRowStart,
  virtualizedDiffEntry,
  diffEntry,
  diffSummary,
  scrollMargin,
  measureElement,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
  virtualWindowHeight,
  imageEstimateEnabled = false,
  rowEstimateRefinementEnabled = false,
  directDomUpdatesEnabled = false,
}: VirtualizedDiffRowProps) {
  const isSelectedFile = useIsSelectedFile(diffEntry?.pathDigest)
  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()

  // Subscribe to the collapsed status directly so this component re-renders when it changes.
  // This ensures shouldMeasure is recalculated and measureElement ref is properly attached/detached.
  const isCollapsed = useCollapsedDiffStatus(virtualizedDiffEntry?.path ?? '')

  // Skeleton/loading states have deterministic height from estimateDiffSize
  const isStillLoading = !virtualizedDiffEntry?.isLoaded || !diffEntry

  if (!virtualizedDiffEntry) return null

  const offset = virtualRowStart - scrollMargin

  // Use `top` positioning instead of `transform: translateY()` to avoid compositing layer
  // explosion on Safari, which promotes every element with any transform to a GPU layer.
  // With direct DOM updates enabled the virtualizer writes `top` itself (in `'position'` mode),
  // so we leave it out of the inline style to avoid fighting over the same property.
  const virtualRowStyle: CSSProperties = directDomUpdatesEnabled ? {} : {top: `${offset}px`}

  // With top positioning the element's DOM position matches its visual position,
  // so sticky headers don't need an offset adjustment.
  const headerStickyOffset = 0

  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')

  // Show skeleton for NotLoaded and Loading states, or when diffEntry hasn't arrived yet
  if (isStillLoading) {
    const skeletonProps = {
      virtualRowIndex,
      virtualRowStyle,
      virtualizedDiffEntry,
      diffSummary,
      commentPreference,
      lineSpacingPreference,
      isCollapsed,
      measureElement,
      headerStickyOffset,
      showUpdatedHeader,
      virtualWindowHeight,
      imageEstimateEnabled,
    }

    return rowEstimateRefinementEnabled ? (
      <RefinedVirtualizedDiffRowSkeleton {...skeletonProps} />
    ) : (
      <VirtualizedDiffRowSkeleton {...skeletonProps} />
    )
  }

  // At this point diffEntry is guaranteed to exist
  const loadedDiffEntry = diffEntry

  return (
    <div
      style={virtualRowStyle}
      className={styles.virtualizedDiffRow}
      ref={measureElement}
      data-index={virtualRowIndex}
      data-path-digest={loadedDiffEntry.pathDigest}
    >
      <PullRequestDiff
        changeType={loadedDiffEntry.status}
        diffLines={loadedDiffEntry.diffLines}
        diffManuallyExpanded={false}
        diffSize={loadedDiffEntry.diffSize}
        headerStickyOffset={headerStickyOffset}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        isBinary={loadedDiffEntry.isBinary}
        isSelected={isSelectedFile}
        isSubmodule={loadedDiffEntry.isSubmodule}
        isTooBig={loadedDiffEntry.isTooBig}
        linesAdded={loadedDiffEntry.linesAdded}
        linesChanged={loadedDiffEntry.linesChanged}
        linesDeleted={loadedDiffEntry.linesDeleted}
        newCommitOid={loadedDiffEntry.newCommitOid}
        newTreeEntry={loadedDiffEntry.newTreeEntry}
        objectId={loadedDiffEntry.objectId}
        oldCommitOid={loadedDiffEntry.oldCommitOid}
        oldTreeEntry={loadedDiffEntry.oldTreeEntry}
        path={loadedDiffEntry.path}
        pathDigest={loadedDiffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        reviewed={loadedDiffEntry.reviewed ?? false}
        richDiff={loadedDiffEntry.richDiff}
        status={loadedDiffEntry.status}
        submodule={loadedDiffEntry.submodule}
        truncatedReason={loadedDiffEntry.truncatedReason}
      />
    </div>
  )
})

type EstimateDiffSizeOptions = Parameters<typeof estimateDiffSize>[0]

interface VirtualizedDiffRowSkeletonProps {
  virtualRowIndex: number
  virtualRowStyle: CSSProperties
  virtualizedDiffEntry: ProgressiveDiffEntry
  diffSummary: PullRequestFileTreeDiff | undefined
  commentPreference: EstimateDiffSizeOptions['commentPreference']
  lineSpacingPreference: EstimateDiffSizeOptions['lineSpacingPreference']
  isCollapsed: boolean
  measureElement: (node: Element | null) => void
  headerStickyOffset: number
  showUpdatedHeader: boolean
  virtualWindowHeight?: number
  imageEstimateEnabled: boolean
  threadSizeInfoMap?: ThreadSizeInfoMap
  isSplitView?: boolean
}

function RefinedVirtualizedDiffRowSkeleton(
  props: Omit<VirtualizedDiffRowSkeletonProps, 'threadSizeInfoMap' | 'isSplitView'>,
) {
  const splitPreference = useSplitPreference()
  const threadSizeInfoMap = useThreadSizeInfoMap()

  return (
    <VirtualizedDiffRowSkeleton
      {...props}
      threadSizeInfoMap={threadSizeInfoMap}
      isSplitView={splitPreference === 'split'}
    />
  )
}

function VirtualizedDiffRowSkeleton({
  virtualRowIndex,
  virtualRowStyle,
  virtualizedDiffEntry,
  diffSummary,
  commentPreference,
  lineSpacingPreference,
  isCollapsed,
  measureElement,
  headerStickyOffset,
  showUpdatedHeader,
  virtualWindowHeight,
  imageEstimateEnabled,
  threadSizeInfoMap,
  isSplitView,
}: VirtualizedDiffRowSkeletonProps) {
  const height = estimateDiffSize({
    diffSummary,
    commentPreference,
    lineSpacingPreference,
    isCollapsed,
    threadSizeInfoMap,
    isSplitView,
    imageEstimateEnabled,
  })
  return (
    <div
      style={virtualRowStyle}
      className={styles.virtualizedDiffRow}
      ref={measureElement}
      data-index={virtualRowIndex}
      data-path-digest={virtualizedDiffEntry.pathDigest}
      data-estimated-height={height}
    >
      <div
        className={clsx(
          diffStyles.diffHeaderWrapper,
          showUpdatedHeader && diffStyles.diffHeaderWrapperWithMiniHeaderAndToolbar,
        )}
        data-diff-header-wrapper
        style={{'--header-sticky-offset': `${headerStickyOffset}px`}}
      >
        <DiffFileHeader
          areLinesExpanded={false}
          canExpandOrCollapseLines={false}
          fileLinkHref={`#diff-${virtualizedDiffEntry.pathDigest}`}
          canToggleRichDiff={false}
          linesAdded={diffSummary?.linesAdded ?? 0}
          linesChanged={diffSummary?.linesChanged ?? 0}
          linesDeleted={diffSummary?.linesDeleted ?? 0}
          newPath={virtualizedDiffEntry.path}
          patchStatus={diffSummary?.changeType ?? ''}
          path={virtualizedDiffEntry.path}
          onToggleExpandAllLines={noop}
          onToggleFileCollapsed={noop}
          onToggleDiffDisplay={noop}
          isInSingleFileMode={false}
        />
      </div>
      <DiffEntryLoadingSkeleton
        ariaLabel={`Loading ${virtualizedDiffEntry.path}`}
        testId={`lazy-load-${virtualizedDiffEntry.path}`}
        id={`diff-${virtualizedDiffEntry.pathDigest}`}
        approximateDiffHeight={height}
        maximumSkeletonHeight={virtualWindowHeight ? virtualWindowHeight * 0.8 : undefined} // Limit skeleton height to 80% of the viewport height so that we see at least some of the surrounding context
      />
    </div>
  )
}

export {MemoizedVirtualizedDiffRow as VirtualizedDiffRow}
